import { useState, useEffect, Fragment } from "react";
import { loadData, storeData, getCached } from "./s3Data";
import { useIsMobile } from "../useIsMobile";

const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const MEALS = ["Breakfast", "Lunch", "Dinner"];

const EMPTY_WEEK = () => {
  const w = {};
  DAYS.forEach(d => {
    w[d] = {};
    MEALS.forEach(m => { w[d][m] = ""; });
  });
  return w;
};

function getWeekStart() {
  const now = new Date();
  const day = now.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  const mon = new Date(now);
  mon.setDate(now.getDate() + diff);
  return mon.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

const M = {
  root: {
    fontFamily: "'DM Mono', 'Courier New', monospace",
    background: "#0f0f11",
    minHeight: "100vh",
    color: "#e8e6e0",
    paddingTop: 70,
  },
  page: {
    padding: "32px 28px",
    maxWidth: 980,
    margin: "0 auto",
  },
  title: {
    fontFamily: "'Fraunces', Georgia, serif",
    fontSize: 26,
    fontWeight: 600,
    color: "#f0ede6",
    letterSpacing: "-0.02em",
    margin: 0,
    marginBottom: 4,
  },
  sub: {
    fontSize: 10,
    color: "#333",
    letterSpacing: "0.1em",
    textTransform: "uppercase",
    marginBottom: 28,
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "100px repeat(7, 1fr)",
    gap: 4,
    minWidth: 0,
  },
  headerCell: {
    background: "#161618",
    border: "1px solid #1e1e22",
    borderRadius: 8,
    padding: "10px 8px",
    textAlign: "center",
  },
  dayName: {
    fontSize: 11,
    fontWeight: 600,
    color: "#f0ede6",
    letterSpacing: "0.02em",
  },
  mealLabel: {
    background: "#111113",
    border: "1px solid #1a1a1d",
    borderRadius: 8,
    padding: "10px 8px",
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
  },
  mealLabelText: {
    fontSize: 9,
    color: "#333",
    letterSpacing: "0.1em",
    textTransform: "uppercase",
    textAlign: "right",
  },
  cell: (editing) => ({
    background: editing ? "#0f1a12" : "#161618",
    border: "1px solid " + (editing ? "#1D9E75" : "#1e1e22"),
    borderRadius: 8,
    padding: "8px",
    minHeight: 56,
    cursor: "pointer",
    transition: "border-color 0.1s",
  }),
  cellText: {
    fontSize: 11,
    color: "#b8b5ae",
    lineHeight: 1.5,
    wordBreak: "break-word",
  },
  cellEmpty: {
    fontSize: 10,
    color: "#1e1e22",
    fontStyle: "italic",
  },
  textarea: {
    width: "100%",
    height: "100%",
    minHeight: 40,
    background: "none",
    border: "none",
    outline: "none",
    resize: "none",
    color: "#e8e6e0",
    fontSize: 11,
    fontFamily: "'DM Mono', monospace",
    padding: 0,
    lineHeight: 1.5,
  },
};

function MealCell({ value, onSave }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);

  function commit() {
    setEditing(false);
    if (draft !== value) onSave(draft);
  }

  return (
    <div
      style={M.cell(editing)}
      onClick={() => { if (!editing) { setDraft(value); setEditing(true); } }}
    >
      {editing ? (
        <textarea
          autoFocus
          style={M.textarea}
          value={draft}
          onChange={e => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={e => { if (e.key === "Escape") { setEditing(false); setDraft(value); } }}
          placeholder="What's for dinner?"
        />
      ) : value ? (
        <span style={M.cellText}>{value}</span>
      ) : (
        <span style={M.cellEmpty}>—</span>
      )}
    </div>
  );
}

export default function MealPlanner() {
  const isMobile = useIsMobile();
  const [week, setWeek] = useState(() => {
    const cached = getCached("meals.json", null);
    if (!cached) return EMPTY_WEEK();
    const merged = EMPTY_WEEK();
    DAYS.forEach(d => { MEALS.forEach(m => { if (cached[d]?.[m] !== undefined) merged[d][m] = cached[d][m]; }); });
    return merged;
  });
  const [saveStatus, setSaveStatus] = useState(null);

  useEffect(() => {
    loadData("meals.json", null).then(data => {
      if (data === null) return;
      const merged = EMPTY_WEEK();
      DAYS.forEach(d => { MEALS.forEach(m => { if (data[d]?.[m] !== undefined) merged[d][m] = data[d][m]; }); });
      setWeek(merged);
    }).catch(() => {});
  }, []);

  async function persist(next) {
    setSaveStatus("saving");
    try {
      await storeData("meals.json", next);
      setSaveStatus("saved");
      setTimeout(() => setSaveStatus(null), 1500);
    } catch (e) {
      console.error("S3 save failed:", e);
      setSaveStatus("error: " + e.name);
    }
  }

  function setMeal(day, meal, value) {
    const next = { ...week, [day]: { ...week[day], [meal]: value } };
    setWeek(next);
    persist(next);
  }

  function clearWeek() {
    const next = EMPTY_WEEK();
    setWeek(next);
    persist(next);
  }

  if (isMobile) {
    return (
      <div style={M.root}>
        <div style={{ padding: "20px 12px", maxWidth: 980, margin: "0 auto" }}>
          <p style={M.title}>Meal Planner</p>
          <p style={{ ...M.sub, marginBottom: 20 }}>
            {saveStatus === "saving" ? "Saving…" : saveStatus === "saved" ? "Saved" : saveStatus === "error" ? "Save failed" : `Week of ${getWeekStart()}`}
          </p>

          {DAYS.map(day => (
            <div key={day} style={{ background: "#161618", border: "1px solid #2a2a2e", borderRadius: 12, padding: "14px 16px", marginBottom: 12 }}>
              <p style={{ fontSize: 12, fontWeight: 600, color: "#f0ede6", margin: 0, marginBottom: 10 }}>{day}</p>
              {MEALS.map(meal => (
                <div key={meal} style={{ marginBottom: 8 }}>
                  <p style={{ fontSize: 9, color: "#333", letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 4 }}>{meal}</p>
                  <MealCell value={week[day][meal]} onSave={v => setMeal(day, meal, v)} />
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div style={M.root}>
      <div style={{ ...M.page }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 4 }}>
          <p style={M.title}>Meal Planner</p>
          <button
            onClick={clearWeek}
            style={{ background: "none", border: "1px solid #2a2a2e", borderRadius: 6, color: "#333", fontSize: 10, padding: "4px 12px", cursor: "pointer", fontFamily: "'DM Mono', monospace", letterSpacing: "0.04em" }}
          >
            Clear week
          </button>
        </div>
        <p style={{ ...M.sub, marginBottom: 20 }}>
          {saveStatus === "saving" ? "Saving…" : saveStatus === "saved" ? "Saved" : saveStatus === "error" ? "Save failed" : `Week of ${getWeekStart()} · click any cell to edit`}
        </p>

        <div style={M.grid}>
          {/* Header row */}
          <div style={{ ...M.headerCell, background: "transparent", border: "none" }} />
          {DAYS.map(d => (
            <div key={d} style={M.headerCell}>
              <p style={M.dayName}>{d.slice(0, 3)}</p>
            </div>
          ))}

          {/* Meal rows */}
          {MEALS.map(meal => (
            <Fragment key={meal}>
              <div style={M.mealLabel}>
                <span style={M.mealLabelText}>{meal}</span>
              </div>
              {DAYS.map(day => (
                <MealCell key={`${day}-${meal}`} value={week[day][meal]} onSave={v => setMeal(day, meal, v)} />
              ))}
            </Fragment>
          ))}
        </div>
      </div>
    </div>
  );
}
